/**
 * DOM 요소를 지금 보이는 모습 그대로 캔버스에 옮긴다. 라이브러리 없이 브라우저의 SVG foreignObject 로 그린다.
 *
 * 1. 사본을 만들고 원본의 계산된 스타일을 요소마다 인라인으로 박는다 — 클래스·변수·애니메이션 중간값까지
 *    지금 값으로 굳는다. 움직임은 멈춘다(이미지는 첫 프레임만 그리므로).
 * 2. ::before·::after 는 사본에 규칙으로 다시 단다. 이미지·캔버스는 data URL 로 바꾼다.
 * 3. 사본이 쓰는 글꼴·굵기의 웹폰트만 @font-face data URL 로 싣는다 — 이미지 안에서는 바깥 글꼴을 못 읽는다.
 *    현재 문자에 해당하는 unicode-range 조각만 받고 인코딩한 결과를 캐시한다.
 *
 * backdrop-filter 는 그리지 못한다(뒤에 비칠 것이 없다). 브라우저가 캔버스를 오염시키면(사파리 등) null.
 */

const PSEUDOS = ['::before', '::after'] as const;
const FROZEN = 'animation-name:none;transition-property:none;';

function inlineStyle(cs: CSSStyleDeclaration): string {
  let out = '';
  for (let i = 0; i < cs.length; i++) {
    const name = cs.item(i);
    // 테마 변수(--*)는 뺀다 — var() 를 쓰던 값은 계산된 값에 이미 풀려 있고, 요소마다 수백 개라
    // 사본이 수 MB 로 불어 그리는 동안 화면이 멈춘다(2026-09-28 실측 5.2MB)
    if (name.startsWith('--')) continue;
    out += `${name}:${cs.getPropertyValue(name)};`;
  }
  return out;
}

function familyList(fontFamily: string): string[] {
  return fontFamily
    .split(',')
    .map((f) => f.trim().replace(/^["']|["']$/g, ''))
    .filter(Boolean);
}

export interface FrozenClone {
  clone: HTMLElement;
  /** 사본에 다시 달 ::before·::after 규칙 */
  pseudoCss: string;
  /** 사본이 쓰는 글꼴 이름 — 글꼴 목록의 뒤 후보까지(한글은 대개 뒤 후보가 그린다) */
  fontFamilies: string[];
  /** 글꼴마다 쓰는 굵기 */
  fontWeights: Map<string, Set<number>>;
  fontText: string;
}

/** 원본의 지금 모습을 인라인 스타일로 굳힌 사본을 만든다 */
export function freezeClone(root: HTMLElement): FrozenClone {
  const clone = root.cloneNode(true) as HTMLElement;
  const originals = [root, ...Array.from(root.querySelectorAll<HTMLElement>('*'))];
  const copies = [clone, ...Array.from(clone.querySelectorAll<HTMLElement>('*'))];
  const fontWeights = new Map<string, Set<number>>();
  let pseudoCss = '';
  let fontText = root.textContent ?? '';

  originals.forEach((orig, i) => {
    const copy = copies[i];
    if (!copy) return;
    const cs = getComputedStyle(orig);
    if (orig instanceof HTMLInputElement || orig instanceof HTMLTextAreaElement) {
      fontText += orig.value + orig.placeholder;
    }
    copy.style.cssText = inlineStyle(cs) + FROZEN;
    // 계산된 폭을 그대로 박으면 소수점 반올림 오차로 마지막 글자가 다음 줄로 넘어간다 — 안쪽 요소는 1px 여유
    const w = parseFloat(cs.width);
    if (i > 0 && cs.width.endsWith('px') && w > 0) copy.style.width = `${w + 1}px`;
    const weight = Number(cs.fontWeight) || 400;
    familyList(cs.fontFamily).forEach((f) => {
      const set = fontWeights.get(f) ?? new Set<number>();
      set.add(weight);
      fontWeights.set(f, set);
    });

    PSEUDOS.forEach((p) => {
      const ps = getComputedStyle(orig, p);
      if (!ps.content || ps.content === 'none' || ps.content === 'normal') return;
      fontText += ps.content.replace(/\\([\da-f]{1,6})\s?/gi, (_, hex: string) =>
        String.fromCodePoint(parseInt(hex, 16))
      );
      copy.setAttribute('data-freeze', String(i));
      pseudoCss += `[data-freeze="${i}"]${p}{${inlineStyle(ps)}${FROZEN}}`;
    });

    // SVG 이미지 안에서는 바깥 그림을 못 불러온다 — 이미 그려진 그림을 data URL 로 옮긴다
    if (orig instanceof HTMLImageElement && orig.complete && orig.naturalWidth > 0) {
      try {
        const c = document.createElement('canvas');
        c.width = orig.naturalWidth;
        c.height = orig.naturalHeight;
        c.getContext('2d')?.drawImage(orig, 0, 0);
        (copy as HTMLImageElement).src = c.toDataURL();
        copy.removeAttribute('srcset');
      } catch {
        // 다른 출처 그림은 그대로 둔다
      }
    }

    if (orig instanceof HTMLCanvasElement) {
      const img = document.createElement('img');
      img.style.cssText = copy.style.cssText;
      try {
        img.src = orig.toDataURL();
      } catch {
        // 오염된 캔버스는 빈칸으로 둔다
      }
      copy.replaceWith(img);
    }
  });

  // 뿌리는 기울기·여백 없이 (0,0) 에 그린다 — 화면의 자리·크기는 그리는 쪽이 맞춘다
  clone.style.transform = 'none';
  clone.style.margin = '0px';
  clone.style.position = 'relative';
  clone.style.left = '0px';
  clone.style.top = '0px';

  return { clone, pseudoCss, fontFamilies: [...fontWeights.keys()], fontWeights, fontText };
}

// 글꼴 파일 → data URL. 같은 파일은 한 번만 받는다.
const fontCache = new Map<string, Promise<string | null>>();

function toDataUrl(url: string): Promise<string | null> {
  let p = fontCache.get(url);
  if (!p) {
    p = fetch(url)
      .then((r) => (r.ok ? r.blob() : null))
      .then(
        (b) =>
          b &&
          new Promise<string | null>((resolve) => {
            const fr = new FileReader();
            fr.onload = () => resolve(typeof fr.result === 'string' ? fr.result : null);
            fr.onerror = () => resolve(null);
            fr.readAsDataURL(b);
          })
      )
      .catch(() => null);
    fontCache.set(url, p);
  }
  return p;
}

// '400' · '45 920' · 'bold' → [최소, 최대]
function weightRange(v: string): [number, number] {
  const named: Record<string, number> = { normal: 400, bold: 700 };
  const parts = v
    .trim()
    .split(/\s+/)
    .map((p) => named[p] ?? Number(p));
  const lo = parts[0] || 400;
  return [lo, parts[1] || lo];
}

// 쓰는 글꼴·굵기의 @font-face 를 data URL 로 바꾸고 URL 인코딩까지 마친 CSS. 같은 조합은 한 번만 만든다.
const encodedFontCache = new Map<string, Promise<string>>();

function containsFontText(range: string, text: string): boolean {
  if (!range) return true;
  const points = Array.from(text + text.normalize('NFC'), (char) => char.codePointAt(0) ?? 0);
  return points.every((cp) =>
    range.split(',').some((part) => {
      const match = /^U\+([\da-f?]+)(?:-([\da-f]+))?$/i.exec(part.trim());
      if (!match?.[1]) return true;
      const first = parseInt(match[1].replace(/\?/g, '0'), 16);
      const last = parseInt(match[2] ?? match[1].replace(/\?/g, 'F'), 16);
      return cp >= first && cp <= last;
    })
  );
}

function encodedFontCss(frozen: FrozenClone): Promise<string> {
  const { fontWeights, fontText } = frozen;
  const graphemes = Array.from(
    new Intl.Segmenter('ko', { granularity: 'grapheme' }).segment(fontText),
    ({ segment }) => segment
  );
  const candidates: {
    rule: CSSFontFaceRule;
    url: string;
    raw: string;
    family: string;
    weights: number[];
    range: string;
  }[] = [];
  for (const sheet of Array.from(document.styleSheets)) {
    let rules: CSSRuleList;
    try {
      rules = sheet.cssRules;
    } catch {
      continue; // 다른 출처
    }
    for (const rule of Array.from(rules)) {
      if (!(rule instanceof CSSFontFaceRule)) continue;
      const family = familyList(rule.style.getPropertyValue('font-family'))[0];
      const used = family ? fontWeights.get(family) : undefined;
      if (!family || !used) continue;
      const [lo, hi] = weightRange(rule.style.getPropertyValue('font-weight') || '400');
      const weights = [...used].filter((w) => w >= lo && w <= hi);
      if (weights.length === 0) continue;
      const range = rule.style.getPropertyValue('unicode-range');
      if (!graphemes.some((text) => containsFontText(range, text))) continue;
      const m = /url\(["']?([^"')]+)["']?\)/.exec(rule.style.getPropertyValue('src'));
      const path = m?.[1];
      if (!m || !path) continue;
      candidates.push({
        rule,
        url: new URL(path, sheet.href ?? document.baseURI).href,
        raw: m[0],
        family,
        weights,
        range,
      });
    }
  }
  const picks = candidates.filter((candidate, index) =>
    graphemes.some(
      (text) =>
        containsFontText(candidate.range, text) &&
        candidate.weights.some(
          (weight) =>
            !candidates
              .slice(index + 1)
              .some(
                (later) =>
                  later.family === candidate.family &&
                  later.weights.includes(weight) &&
                  containsFontText(later.range, text)
              )
        )
    )
  );
  const key = picks.map((p) => p.url).join('|');
  let p = encodedFontCache.get(key);
  if (!p) {
    p = Promise.all(
      picks.map(({ rule, url, raw }) =>
        toDataUrl(url).then((data) => (data ? rule.cssText.replace(raw, `url("${data}")`) : ''))
      )
    ).then((css) => encodeURIComponent(css.join('\n')));
    encodedFontCache.set(key, p);
  }
  return p;
}

/** 쓰는 글꼴을 미리 받아 인코딩해 둔다 — 찍는 순간의 대기를 줄인다 */
export function warmRasterFonts(root: HTMLElement): void {
  void encodedFontCss(freezeClone(root));
}

/**
 * 요소를 layout 크기(변형 전)로 그린 캔버스. scale 은 픽셀 밀도.
 * 브라우저가 막으면 null — 부르는 쪽은 예전 동작으로 돌아가야 한다.
 */
export async function rasterizeElement(
  el: HTMLElement,
  scale = 1
): Promise<HTMLCanvasElement | null> {
  try {
    const cs = getComputedStyle(el);
    const width = parseFloat(cs.width);
    const height = parseFloat(cs.height);
    if (!(width > 0 && height > 0)) return null;

    const frozen = freezeClone(el);
    const { clone, pseudoCss } = frozen;
    const fontCss = await encodedFontCss(frozen);
    const xhtml = new XMLSerializer().serializeToString(clone);
    const head =
      `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}">` +
      `<foreignObject width="100%" height="100%">` +
      `<div xmlns="http://www.w3.org/1999/xhtml"><style>`;
    const tail = `${pseudoCss}</style>${xhtml}</div></foreignObject></svg>`;

    const img = new Image();
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(head)}${fontCss}${encodeURIComponent(tail)}`;
    await img.decode();

    const canvas = document.createElement('canvas');
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const g = canvas.getContext('2d');
    if (!g) return null;
    g.scale(scale, scale);
    g.drawImage(img, 0, 0, width, height);
    g.getImageData(0, 0, 1, 1); // 오염됐으면 여기서 던진다
    return canvas;
  } catch {
    return null;
  }
}
